import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            /* Mobile Breadcrumb for all pages */
            max-width: 1280px;
            /* margin: 0 auto; */
            > a {
                white-space: nowrap;
            }
            @media ((min-width: 768px) and (max-width: 1024px)) {
                &.tablet-breadcrumb {
                    margin-top: 15px;
                    a {
                        display: none !important;
                        &:last-of-type {
                            display: inline-flex !important;
                        }
                        &:before {
                            content: "<" !important;
                            color: #000000 !important;
                            padding-right: 10px !important;
                            font-size: 18px !important;
                        }
                    }
                    > span {
                        display: none !important;
                    }
                }
            }
            &.mobile-breadcrumb {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                a {
                                    display: none;
                                    &:last-of-type {
                                        display: inline-flex;
                                    }
                                    &:before {
                                        content: "<";
                                        color: #000000;
                                        padding-right: 10px;
                                        font-size: 18px;
                                    }
                                }
                                > span {
                                    display: none;
                                }
                            `,
                            css`
                                margin-bottom: 25px;
                                a {
                                    display: none;
                                    &:last-of-type {
                                        display: inline-flex;
                                    }
                                    &:before {
                                        content: "<";
                                        color: #000000;
                                        padding-right: 10px;
                                        font-size: 18px;
                                    }
                                }
                                > span {
                                    display: none;
                                }
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    )}
            }
        `,
    },
};

type Props = ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    const links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);
    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const selectStateEl: HTMLSelectElement | null = document.querySelector(
                '[data-test-selector="pageBreadcrumbs"]',
            );
            if (selectStateEl) {
                selectStateEl.classList.add("mobile-breadcrumb");
                selectStateEl.classList.add("tablet-breadcrumb");
            }
        }
    });

    return <Breadcrumbs links={links} data-test-selector="pageBreadcrumbs" {...pageBreadcrumbStyles.breadcrumbs} />;
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));
    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({
                children: currentLink.type === "HomePage" ? translate("Home_Breadcrumb") : currentLink.title,
                href: currentLink.url,
                type: currentLink.type,
            });
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
